class CollectionFilters {
    constructor() {
        this.container = document.querySelector('#CollectionFilters');
        this.openBtn = document.querySelector('[data-filter-open]');
        this.closeBtn = document.querySelector('[data-filter-close]');
        this.form = document.querySelector('#CollectionFiltersForm');
        this.sortSelect = document.querySelector('#SortBy');
        this.productGrid = document.querySelector('#ProductGrid');
        this.contentArea = document.querySelector('#MainCollectionContent');
        this.sectionId = document.querySelector('[data-section-id]')?.dataset.sectionId;

        if (!this.container) return;

        this.bindEvents();
    }

    bindEvents() {
        if (this.openBtn) {
            this.openBtn.addEventListener('click', () => this.open());
        }

        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
        }

        document.addEventListener('keyup', (event) => {
            if (event.code === 'Escape' && this.container.classList.contains('is-open')) {
                this.close();
            }
        });

        if (this.form) {
            this.form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
                checkbox.addEventListener('change', () => this.submitFilters());
            });

            if (this.sortSelect) {
                this.sortSelect.addEventListener('change', () => this.submitFilters());
            }

            const priceInputs = this.form.querySelectorAll('input[type="number"]');
            priceInputs.forEach(input => {
                let timeout = null;
                input.addEventListener('input', () => {
                    clearTimeout(timeout);
                    timeout = setTimeout(() => this.submitFilters(), 800);
                });
            });
        }

        // AJAX filter pill removal
        document.addEventListener('click', (e) => {
            const pill = e.target.closest('[data-filter-remove]');
            if (pill) {
                e.preventDefault();
                this.fetchAndReplace(pill.href);
            }
        });

        // AJAX concern pill navigation
        document.addEventListener('click', (e) => {
            const concern = e.target.closest('[data-concern-pill]');
            if (concern) {
                e.preventDefault();
                this.fetchAndReplace(concern.href);
            }
        });

        // Load more button
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-load-more]');
            if (btn) {
                e.preventDefault();
                this.loadMore(btn);
            }
        });

        // AJAX quick-add to cart
        document.addEventListener('submit', (e) => {
            const form = e.target.closest('[data-quick-add]');
            if (!form) return;
            e.preventDefault();
            this.quickAdd(form);
        });

        // Handle browser back/forward
        window.addEventListener('popstate', () => {
            this.fetchAndReplace(window.location.href);
        });
    }

    open() {
        this.container.classList.add('is-open');
        this.container.setAttribute('aria-hidden', 'false');
        document.body.classList.add('menu-open');
        if (this.closeBtn) this.closeBtn.focus();
        this.trapFocus(this.container);
    }

    close() {
        this.container.classList.remove('is-open');
        this.container.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('menu-open');
        this.releaseFocus();
        if (this.openBtn) this.openBtn.focus();
    }

    trapFocus(element) {
        const focusable = element.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        this.firstFocusable = focusable[0];
        this.lastFocusable = focusable[focusable.length - 1];

        this.focusTrapHandler = (e) => {
            if (e.key !== 'Tab') return;
            if (e.shiftKey) {
                if (document.activeElement === this.firstFocusable) {
                    e.preventDefault();
                    this.lastFocusable.focus();
                }
            } else {
                if (document.activeElement === this.lastFocusable) {
                    e.preventDefault();
                    this.firstFocusable.focus();
                }
            }
        };
        element.addEventListener('keydown', this.focusTrapHandler);
    }

    releaseFocus() {
        if (this.focusTrapHandler && this.container) {
            this.container.removeEventListener('keydown', this.focusTrapHandler);
        }
    }

    async submitFilters() {
        const formData = new FormData(this.form);
        const params = new URLSearchParams(formData).toString();
        const url = `${window.location.pathname}?${params}`;
        await this.fetchAndReplace(url, true);
    }

    async fetchAndReplace(url, pushState = true) {
        if (this.productGrid) {
            this.productGrid.classList.add('is-loading');
        }

        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(res.status);
            const html = await res.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Replace product grid
            this.replaceElement('#ProductGrid', doc);
            // Replace product count
            this.replaceElement('#ProductCount', doc);
            // Replace active filters
            this.replaceElement('#ActiveFilters', doc);
            // Replace load more
            this.replaceElement('#LoadMore', doc);
            // Replace featured product
            this.replaceElement('#FeaturedProduct', doc);
            // Replace concern pills
            this.replaceElement('#ConcernPills', doc);

            // Update reference
            this.productGrid = document.querySelector('#ProductGrid');

            if (pushState) {
                history.pushState(null, '', url);
            }

            // Announce update to screen readers
            const countEl = document.querySelector('#ProductCount');
            if (countEl) {
                countEl.textContent = countEl.textContent;
            }

            // Re-init scroll animations for new items
            if (typeof initScrollObserver === 'function') {
                initScrollObserver();
            }

            // Close mobile drawer if open
            if (this.container.classList.contains('is-open')) {
                this.close();
            }
        } catch (err) {
            // Fallback to full reload
            window.location.href = url;
        }
    }

    replaceElement(selector, doc) {
        const oldEl = document.querySelector(selector);
        const newEl = doc.querySelector(selector);

        if (oldEl && newEl) {
            oldEl.replaceWith(newEl.cloneNode(true));
        } else if (oldEl && !newEl) {
            oldEl.remove();
        } else if (!oldEl && newEl) {
            // Insert active filters before the grid
            if (selector === '#ActiveFilters') {
                const toolbar = document.querySelector('.main-collection__toolbar');
                if (toolbar) {
                    toolbar.insertAdjacentElement('afterend', newEl.cloneNode(true));
                }
            }
        }
    }

    async loadMore(btn) {
        const nextUrl = btn.dataset.nextUrl;
        if (!nextUrl) return;

        const originalText = btn.textContent;
        btn.textContent = 'Loading...';
        btn.disabled = true;

        try {
            const res = await fetch(nextUrl);
            if (!res.ok) throw new Error(res.status);
            const html = await res.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Append new product items to grid
            const newGrid = doc.querySelector('#ProductGrid');
            const currentGrid = document.querySelector('#ProductGrid');
            if (newGrid && currentGrid) {
                const newItems = newGrid.querySelectorAll('.main-collection__grid-item');
                newItems.forEach(item => {
                    currentGrid.appendChild(item.cloneNode(true));
                });
            }

            // Update or remove load-more button
            const newLoadMore = doc.querySelector('[data-load-more]');
            const newCount = doc.querySelector('.main-collection__load-more-count');
            const currentCount = document.querySelector('.main-collection__load-more-count');

            if (newLoadMore) {
                btn.dataset.nextUrl = newLoadMore.dataset.nextUrl;
                btn.textContent = originalText;
                btn.disabled = false;
                if (newCount && currentCount) {
                    currentCount.textContent = newCount.textContent;
                }
            } else {
                btn.closest('.main-collection__load-more')?.remove();
            }

            // Re-init scroll animations
            if (typeof initScrollObserver === 'function') {
                initScrollObserver();
            }
        } catch (err) {
            btn.textContent = originalText;
            btn.disabled = false;
            // Fallback: navigate to next page
            window.location.href = nextUrl;
        }
    }

    async quickAdd(form) {
        const btn = form.querySelector('button[type="submit"]');
        if (!btn) return;

        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        try {
            const formData = new FormData(form);
            await CartAPI.addToCart(formData);
            document.dispatchEvent(new CustomEvent('cart:item-added'));

            btn.textContent = 'Added!';
            btn.classList.add('btn--atc-success');

            setTimeout(() => {
                btn.textContent = originalText;
                btn.disabled = false;
                btn.classList.remove('btn--atc-success');
            }, 1500);
        } catch (err) {
            btn.textContent = 'Error';
            setTimeout(() => {
                btn.textContent = originalText;
                btn.disabled = false;
            }, 1500);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new CollectionFilters();
});
